import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import AddBook from './AddBook'
import MarkAsRead from './MarkAsRead'
import StartReading from './StartReading'
import BookSearchPreview from './BookSearchPreview'
import { formatDate } from '../utils/dateFormat'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface ToReadBook {
  id: number
  notes?: string
  added_at: string
  book: Book
}

interface ToReadListProps {
  user: {
    id: number
    name: string
    preferred_affiliate_provider?: string | null
  }
  onBookStarted?: () => void
  refreshTrigger?: number
  limit?: number  // Limit number of books shown (for dashboard preview)
  showSeeAll?: boolean  // Show "See All" button
  onSeeAll?: () => void  // Callback for "See All" button
  onProviderSelected?: (provider: string) => void
}

// Sortable book item component
function SortableBookItem({
  trb,
  onPreview,
  onStartReading,
  onMarkAsRead,
  onDelete,
  deletingId,
  openMenuId,
  setOpenMenuId,
}: {
  trb: ToReadBook
  onPreview: (book: Book) => void
  onStartReading: (trb: ToReadBook) => void
  onMarkAsRead: (trb: ToReadBook) => void
  onDelete: (id: number) => void
  deletingId: number | null
  openMenuId: number | null
  setOpenMenuId: (id: number | null) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: trb.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 hover:bg-gray-50 transition-colors group"
    >
      <div className="flex gap-4">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          title="Drag to reorder"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </button>

        {/* Clickable Book Info */}
        <button
          onClick={() => onPreview(trb.book)}
          className="flex gap-4 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          {trb.book.cover_url ? (
            <img
              src={trb.book.cover_url}
              alt={trb.book.title}
              className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1">{trb.book.title}</h3>
            <p className="text-sm text-gray-600 mb-2">by {trb.book.author}</p>
            {trb.notes && (
              <p className="text-sm text-gray-500 italic mb-2">"{trb.notes}"</p>
            )}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Added {formatDate(trb.added_at)}</span>
              {trb.book.first_publish_year && (
                <>
                  <span>•</span>
                  <span>{trb.book.first_publish_year}</span>
                </>
              )}
              {trb.book.number_of_pages && (
                <>
                  <span>•</span>
                  <span>{trb.book.number_of_pages} pages</span>
                </>
              )}
            </div>
            <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
          </div>
        </button>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setOpenMenuId(openMenuId === trb.id ? null : trb.id)
            }}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actions"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          {openMenuId === trb.id && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setOpenMenuId(null)}
              />
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <button
                  onClick={() => {
                    onStartReading(trb)
                    setOpenMenuId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Start Reading
                </button>
                <button
                  onClick={() => {
                    onMarkAsRead(trb)
                    setOpenMenuId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Mark as Read
                </button>
                <button
                  onClick={() => {
                    onDelete(trb.id)
                    setOpenMenuId(null)
                  }}
                  disabled={deletingId === trb.id}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deletingId === trb.id ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function ToReadList({ user, onBookStarted, refreshTrigger, limit, showSeeAll = false, onSeeAll, onProviderSelected }: ToReadListProps) {
  const [toReadBooks, setToReadBooks] = useState<ToReadBook[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddBook, setShowAddBook] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [showMarkAsRead, setShowMarkAsRead] = useState(false)
  const [showStartReading, setShowStartReading] = useState(false)
  const [selectedBook, setSelectedBook] = useState<ToReadBook | null>(null)
  const [previewBook, setPreviewBook] = useState<Book | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    fetchToReadBooks()
  }, [])

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchToReadBooks()
    }
  }, [refreshTrigger])

  const fetchToReadBooks = async () => {
    try {
      const response = await fetch('/api/v1/to_read_books')
      if (response.ok) {
        const data = await response.json()
        setToReadBooks(data.to_read_books)
      }
    } catch (err) {
      console.error('Failed to fetch to-read books:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    setDeletingId(id)
    try {
      const response = await fetchWithCsrf(`/api/v1/to_read_books/${id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setToReadBooks(toReadBooks.filter(trb => trb.id !== id))
      }
    } catch (err) {
      console.error('Failed to delete book:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = toReadBooks.findIndex(trb => trb.id === active.id)
    const newIndex = toReadBooks.findIndex(trb => trb.id === over.id)

    const newOrder = arrayMove(toReadBooks, oldIndex, newIndex)
    setToReadBooks(newOrder)

    // Save the new order to the server
    try {
      await fetchWithCsrf('/api/v1/to_read_books/reorder', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          book_ids: newOrder.map(trb => trb.id),
        }),
      })
    } catch (err) {
      console.error('Failed to save book order:', err)
      // Revert to original order on error
      fetchToReadBooks()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  // Apply limit if specified
  const displayedBooks = limit ? toReadBooks.slice(0, limit) : toReadBooks
  const hasMore = limit && toReadBooks.length > limit

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Want to Read</h2>
        {showSeeAll && hasMore && (
          <button
            onClick={onSeeAll}
            className="text-indigo-600 hover:text-indigo-700 font-medium text-sm flex items-center gap-1"
          >
            See All ({toReadBooks.length})
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {displayedBooks.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={displayedBooks.map(trb => trb.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 divide-y divide-gray-200">
              {displayedBooks.map(trb => (
                <SortableBookItem
                  key={trb.id}
                  trb={trb}
                  onPreview={setPreviewBook}
                  onStartReading={(trb) => {
                    setSelectedBook(trb)
                    setShowStartReading(true)
                  }}
                  onMarkAsRead={(trb) => {
                    setSelectedBook(trb)
                    setShowMarkAsRead(true)
                  }}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No books yet</h3>
          <p className="text-gray-600 mb-6">Start building your reading list!</p>
          <button
            onClick={() => setShowAddBook(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Your First Book
          </button>
        </div>
      )}

      {/* Add Book Modal */}
      {showAddBook && (
        <AddBook
          onClose={() => setShowAddBook(false)}
          onSuccess={() => {
            setShowAddBook(false)
            fetchToReadBooks()
          }}
          defaultStatus="want_to_read"
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={onProviderSelected}
        />
      )}

      {/* Start Reading Modal */}
      {showStartReading && selectedBook && (
        <StartReading
          book={selectedBook.book}
          toReadBookId={selectedBook.id}
          onClose={() => {
            setShowStartReading(false)
            setSelectedBook(null)
          }}
          onSuccess={() => {
            setShowStartReading(false)
            setSelectedBook(null)
            fetchToReadBooks()
            onBookStarted?.()
          }}
        />
      )}

      {/* Mark as Read Modal */}
      {showMarkAsRead && selectedBook && (
        <MarkAsRead
          book={selectedBook.book}
          toReadBookId={selectedBook.id}
          onClose={() => {
            setShowMarkAsRead(false)
            setSelectedBook(null)
          }}
          onSuccess={() => {
            setShowMarkAsRead(false)
            setSelectedBook(null)
            fetchToReadBooks()
          }}
        />
      )}

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => setPreviewBook(null)}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={onProviderSelected}
        />
      )}
    </div>
  )
}
